import { useState, useEffect, useCallback } from 'react';

const GRAMS_PER_TROY_OUNCE = 31.1034768;
// Indian import duty on gold: 10% Basic Customs Duty + 5% AIDC (effective 13 May 2026).
// Update this if the duty changes. Published Indian gold rates exclude 3% GST, as does this.
export const INDIA_GOLD_IMPORT_DUTY = 0.15;

const chartUrl = (symbol) =>
  `/api/yahoo-chart?url=${encodeURIComponent(
    `https://query1.finance.yahoo.com/v8/finance/chart/${encodeURIComponent(symbol)}?interval=1d&range=1d`
  )}`;

const fetchQuote = async (symbol) => {
  const response = await fetch(chartUrl(symbol));
  if (!response.ok) {
    throw new Error(`Quote request for ${symbol} failed with status ${response.status}`);
  }
  const data = await response.json();
  const meta = data?.chart?.result?.[0]?.meta;
  if (!meta?.regularMarketPrice) {
    throw new Error(`No price returned for ${symbol}`);
  }
  return meta;
};

// Live 24K gold price in INR per gram for India, derived from COMEX gold futures
// (USD/oz) and the USD/INR rate, plus Indian import duty. Like published Indian
// rates it excludes GST; local dealer premiums can add roughly another 0.5-1%.
export const fetchLiveGoldPricePerGram = async () => {
  const [gold, usdInr] = await Promise.all([fetchQuote('GC=F'), fetchQuote('USDINR=X')]);
  const internationalPerGram = (gold.regularMarketPrice * usdInr.regularMarketPrice) / GRAMS_PER_TROY_OUNCE;
  const pricePerGram = internationalPerGram * (1 + INDIA_GOLD_IMPORT_DUTY);
  const previousPerGram = gold.chartPreviousClose && usdInr.chartPreviousClose
    ? ((gold.chartPreviousClose * usdInr.chartPreviousClose) / GRAMS_PER_TROY_OUNCE) * (1 + INDIA_GOLD_IMPORT_DUTY)
    : null;

  return {
    pricePerGram,
    internationalPerGram,
    changePercent: previousPerGram ? ((pricePerGram - previousPerGram) / previousPerGram) * 100 : null,
    usdPerOunce: gold.regularMarketPrice,
    usdInr: usdInr.regularMarketPrice,
    asOf: new Date(Math.max(gold.regularMarketTime, usdInr.regularMarketTime) * 1000),
  };
};

export const useLiveGoldPrice = () => {
  const [goldPrice, setGoldPrice] = useState(null);
  const [goldPriceLoading, setGoldPriceLoading] = useState(true);
  const [goldPriceError, setGoldPriceError] = useState(null);

  const refreshGoldPrice = useCallback(async () => {
    setGoldPriceLoading(true);
    setGoldPriceError(null);
    try {
      setGoldPrice(await fetchLiveGoldPricePerGram());
    } catch (error) {
      console.error('Error fetching live gold price:', error);
      setGoldPriceError(error.message || 'Failed to fetch live gold price');
    } finally {
      setGoldPriceLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshGoldPrice();
  }, [refreshGoldPrice]);

  return { goldPrice, goldPriceLoading, goldPriceError, refreshGoldPrice };
};
